'use client';

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import { useParams, useSearchParams } from 'react-router-dom';

import axiosInstance from '@/lib/axios';
import { reportCreatePostFailure } from '@/lib/capture-create-post-error';
import { uploadFilesToVertex } from '@/lib/vertex-upload';
import { FormSubmitOverlay } from '@/components/forms/form-submit-overlay';
import { useCommunityMembers } from '@/hooks/use-community-members';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FormInputField } from '@/components/forms/form-input-field';
import { FormTextareaField } from '@/components/forms/form-textarea-field';
import { FormComboboxField } from '@/components/forms/form-combobox-field';
import { SectionLabel } from '@/components/forms/section-label';
import { FileAttachmentList } from '@/components/forms/file-attachment-list';
import { FileUploadButton } from '@/components/forms/file-upload-button';

const testimonialFormSchema = z.object({
  memberId: z.string().min(1, 'Please select a member'),
  title: z.string().min(5, 'Title must be at least 5 characters'),
  notes: z.string().min(10, 'Please describe the service in detail').max(1000),
});

interface TestimonialFormProps {
  onCancel: () => void;
  onSubmit: (postId: string) => void;
}

export function TestimonialForm({ onCancel, onSubmit: parentSubmit }: TestimonialFormProps) {
  const { communityId } = useParams<{ communityId: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [docs, setDocs] = useState<File[]>([]);
  const [submitPhase, setSubmitPhase] = useState<'idle' | 'uploading' | 'saving'>('idle');

  const { members, currentUserId } = useCommunityMembers(communityId!);

  const memberOptions = members
    .filter((m) => m.id !== currentUserId)
    .map((m) => ({
      label: m.businessName ? `${m.name} (${m.businessName})` : m.name,
      value: m.id,
    }));

  const form = useForm<z.infer<typeof testimonialFormSchema>>({
    resolver: zodResolver(testimonialFormSchema),
    defaultValues: {
      memberId: '',
      title: '',
      notes: '',
    },
  });

  const [searchParams] = useSearchParams();
  const prefillMemberId = searchParams.get('memberId');

  useEffect(() => {
    if (prefillMemberId) {
      form.setValue('memberId', prefillMemberId);
    }
  }, [prefillMemberId, form]);

  const onSubmit = async (values: z.infer<typeof testimonialFormSchema>) => {
    setIsSubmitting(true);
    setSubmitPhase(docs.length > 0 ? 'uploading' : 'saving');

    try {
      const documentUrls = docs.length > 0 ? await uploadFilesToVertex(docs, 'testimonials') : [];

      const formData = new FormData();
      formData.append('memberId', values.memberId);
      formData.append('title', values.title);
      formData.append('notes', values.notes);
      formData.append('attachments', JSON.stringify(documentUrls));

      setSubmitPhase('saving');
      const { data } = await axiosInstance.post(
        `/api/communities/${communityId}/posts/testimonial`,
        formData
      );
      toast.success('Testimonial Shared Successfully!');
      parentSubmit(data.postId);
    } catch (error) {
      toast.error(
        reportCreatePostFailure({
          kind: 'testimonial',
          error,
          userId: currentUserId,
          communityId,
          request: {
            memberId: values.memberId,
            titleLength: values.title.length,
            notesLength: values.notes.length,
            documentCount: docs.length,
          },
          fallbackMessage: 'Failed to share testimonial. Please try again.',
        })
      );
    } finally {
      setIsSubmitting(false);
      setSubmitPhase('idle');
    }
  };

  const removeFile = (
    index: number,
    setFileState: React.Dispatch<React.SetStateAction<File[]>>
  ) => {
    setFileState((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <>
      <FormSubmitOverlay
        open={isSubmitting}
        title={submitPhase === 'uploading' ? 'Uploading files...' : 'Sharing testimonial...'}
        description={
          submitPhase === 'uploading'
            ? 'Sending documents through secure media upload. Please keep this screen open.'
            : 'Saving your testimonial.'
        }
      />
      <Card className="mx-auto w-full max-w-5xl animate-in slide-in-from-bottom-5 border-0 bg-background/50 shadow-none backdrop-blur-md duration-300 md:border md:border-border md:shadow-2xl">
        <CardHeader className="px-3 md:px-6">
          <CardTitle className="text-2xl font-black tracking-tight text-foreground">
            Share Testimonial
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Recommend a member&apos;s service to the community.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-3 md:px-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormComboboxField
                control={form.control}
                name="memberId"
                label="Select Member"
                placeholder="Click to choose a member"
                required
                options={memberOptions}
              />

              <FormInputField
                control={form.control}
                name="title"
                label="Title"
                placeholder="e.g., Excellent Web Design Service"
                required
              />

              <FormTextareaField
                control={form.control}
                name="notes"
                label="What services were offered and how were you benefited?"
                placeholder="Describe your experience... Use @ for a member or business, # for a category"
                maxLength={1000}
                required
                mentions
              />

              <div className="space-y-2">
                <SectionLabel>Reference Documents (Optional)</SectionLabel>
                <div className="flex items-center gap-4">
                  <FileUploadButton
                    id="testimonial-doc-upload"
                    accept=".pdf,.doc,.docx,.txt,image/*"
                    onFiles={(files) => setDocs((prev) => [...prev, ...files])}
                    label="Attach Docs"
                    icon={Paperclip}
                  />
                </div>
                <FileAttachmentList files={docs} onRemove={(i) => removeFile(i, setDocs)} />
              </div>

              <div className="flex flex-col-reverse justify-end gap-3 border-t border-border pt-6 md:flex-row">
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full text-muted-foreground hover:bg-muted hover:text-foreground md:w-auto"
                  onClick={onCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary px-8 text-white shadow-lg shadow-primary/20 hover:bg-primary/90 md:w-auto"
                >
                  {isSubmitting ? 'Sharing...' : 'Share Testimonial'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </>
  );
}
